import { useEffect, useLayoutEffect, useRef, type ReactElement } from 'react'
import type { Route } from '@metro/routing'
import { RouteVariantRow, type LineDisplay } from './RouteVariantRow'
import styles from './RouteResultsList.module.css'

/**
 * Props for {@link RouteResultsList}.
 */
export interface RouteResultsListProps {
  /** All route variants returned by the algo. Render order = list order. */
  readonly routes: readonly Route[]
  /** Selected variant index. Clamped at render time to `[0, routes.length-1]`. */
  readonly selectedIndex: number
  /** Maps line_id → display info. Forwarded to each {@link RouteVariantRow}. */
  readonly lineLookup: ReadonlyMap<string, LineDisplay>
  /** Fired with the new index on tap. Caller dispatches `RouteSelectedVariantChanged`. */
  readonly onSelect: (index: number) => void
  /** Test seam for the clock used by RouteVariantRow. */
  readonly clock?: () => Date
  /**
   * Layout variant. `phone` (default) renders the horizontal scroll-
   * snap carousel of compact cards used inside the bottom RouteCard
   * panel. `tablet` renders a vertical column of full-width cards used
   * inside the desktop / tablet sidebar — Figma spec TASK-275 §4.2.
   */
  readonly variant?: 'phone' | 'tablet'
  /**
   * Accessible-label builder for the list — `{count}` route options.
   * @defaultValue ``(n) => `${n} route options` ``
   */
  readonly resultsListAria?: (count: number) => string
}

/**
 * Horizontal scroll-snap list of {@link RouteVariantRow} cards for the
 * routeStatus=results state. UI design spec §4 (variant switcher).
 *
 * Behavior:
 * - Each row is a `<li>` whose `scroll-snap-align: center` lines it up
 *   with the container's center after a swipe stops.
 * - Tap on a row fires `onSelect(idx)` only. The parent re-renders
 *   with the new `selectedIndex` and the selection-change effect
 *   below performs the smooth scroll. Tap on the already-selected
 *   row is a reducer no-op (no re-render → no scroll).
 * - When `selectedIndex` changes externally (persisted variant restore),
 *   the matching row is smooth-scrolled into the center via `useEffect`.
 * - Initial mount uses `useLayoutEffect` with `behavior: 'auto'` so the
 *   user does not see the list jump after paint.
 *
 * Returns `null` when `routes` is empty — the empty state lives in a
 * sibling component (Task 18). Out-of-range `selectedIndex` is clamped
 * defensively (the reducer already clamps but UI mustn't crash on
 * malformed input).
 */
export function RouteResultsList({
  routes,
  selectedIndex,
  lineLookup,
  onSelect,
  clock,
  variant = 'phone',
  resultsListAria = (count: number) => `${count} route options`,
}: RouteResultsListProps): ReactElement | null {
  const listRef = useRef<HTMLUListElement>(null)
  const itemRefs = useRef<Array<HTMLLIElement | null>>([])

  // Defensive clamp — reducer already does this, but a wrong prop must
  // not crash the UI. Empty list early-returns below, so the clamp is
  // only reached for non-empty lists.
  const clampedIndex =
    routes.length === 0 ? 0 : Math.min(Math.max(selectedIndex, 0), routes.length - 1)

  // Initial mount: jump the selected row into the center immediately
  // (no smooth animation — user shouldn't see the list shift after
  // paint). Subsequent selectedIndex changes are handled by the
  // smooth-scroll effect below.
  useLayoutEffect(() => {
    if (routes.length === 0) return
    const target = itemRefs.current[clampedIndex]
    if (target == null) return
    target.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
      inline: variant === 'tablet' ? 'nearest' : 'center',
    })
    // Mount-only — exhaustive deps would re-run on every selection
    // change and undo the smooth-scroll effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // External selectedIndex change (e.g. persisted variant restored on
  // session start, or a sibling component dispatches the reducer
  // event): smooth-scroll the matching row into view. Phone uses
  // horizontal centering; tablet uses vertical-only nearest so the
  // sidebar's vertical scroll behaves naturally.
  useEffect(() => {
    if (routes.length === 0) return
    const target = itemRefs.current[clampedIndex]
    if (target == null) return
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: variant === 'tablet' ? 'nearest' : 'center',
    })
    // itemRefs is a ref — stable across renders, intentionally omitted.
  }, [clampedIndex, routes.length, variant])

  if (routes.length === 0) return null

  const listClass = variant === 'tablet' ? styles.listTablet : styles.list
  const itemClass = variant === 'tablet' ? styles.itemTablet : styles.item

  return (
    <ul
      ref={listRef}
      className={listClass}
      data-testid="route-results-list"
      aria-label={resultsListAria(routes.length)}
    >
      {routes.map((route, idx) => {
        // Phone variant gets inline edge gaps so the first/last cards
        // can scroll away from the panel edge instead of pinning. Tablet
        // doesn't need either — full-width vertical cards.
        const phoneEdgeStyle =
          variant === 'phone'
            ? {
                marginLeft: idx === 0 ? 16 : undefined,
                marginRight: idx === routes.length - 1 ? 16 : undefined,
              }
            : undefined
        return (
          <li
            key={route.id}
            ref={(el) => {
              itemRefs.current[idx] = el
            }}
            className={itemClass}
            data-testid={`route-results-list-item-${idx}`}
            style={phoneEdgeStyle}
          >
            <RouteVariantRow
              route={route}
              isSelected={idx === clampedIndex}
              lineLookup={lineLookup}
              onTap={() => {
                onSelect(idx)
              }}
              clock={clock}
              variant={variant}
              variantIndex={idx + 1}
              totalVariants={routes.length}
            />
          </li>
        )
      })}
    </ul>
  )
}
